import { motion } from "framer-motion";
import { ArrowUpRight, ImageOff } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { isVideoUrl } from "./HeroSection";
import AutoVideo from "./AutoVideo";

const EASE = [0.16, 1, 0.3, 1];
const IDS = ["wellness-journey", "transformation"];
const LINK_TESTIDS = ["wellness-journey-section-link", "transformation-section-link"];

export default function TwinImpactSections() {
  const { content } = useContent();
  const sections = content.twin.sections;

  return (
    <section className="py-24 sm:py-32" data-testid="twin-sections">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono-lux text-xs uppercase tracking-[0.3em] text-gold mb-14 text-center"
          data-testid="twin-eyebrow"
        >
          {content.twin.eyebrow}
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {sections.map((s, i) => {
            const id = IDS[i] || `twin-section-${i}`;
            return (
              <motion.article
                key={id}
                id={id}
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: EASE, delay: i * 0.15 }}
                className="group relative border border-[rgba(212,175,55,0.2)] bg-surface overflow-hidden hover:border-[rgba(212,175,55,0.5)] transition-colors duration-500"
                data-testid={`${id}-card`}
              >
                <div className="relative h-60 sm:h-72 overflow-hidden">
                  {!s.img ? (
                    <div
                      className="w-full h-full bg-[#0A0A0C] flex items-center justify-center"
                      data-testid={`${id}-image`}
                      role="img"
                      aria-label="No media uploaded"
                    >
                      <ImageOff size={28} className="text-[rgba(212,175,55,0.35)]" aria-hidden="true" />
                    </div>
                  ) : isVideoUrl(s.img) ? (
                    <AutoVideo
                      src={s.img}
                      className="w-full h-full object-cover brightness-[0.55] grayscale group-hover:brightness-[0.75] group-hover:scale-105 transition-all duration-[1.2s] ease-out"
                      testId={`${id}-image`}
                    />
                  ) : (
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover brightness-[0.55] grayscale group-hover:brightness-[0.75] group-hover:scale-105 transition-all duration-[1.2s] ease-out"
                      data-testid={`${id}-image`}
                    />
                  )}
                  <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                  <span className="absolute top-5 left-5 font-mono-lux text-[10px] uppercase tracking-[0.3em] text-gold bg-[rgba(7,7,8,0.7)] backdrop-blur-sm border border-[rgba(212,175,55,0.35)] px-3 py-1.5">
                    {s.tag}
                  </span>
                </div>
                <div className="p-8 sm:p-10">
                  <h3 className="font-serif-lux text-2xl sm:text-3xl tracking-tight text-[#F7F7F8]" data-testid={`${id}-title`}>
                    {s.title}
                  </h3>
                  <p className="mt-2 font-serif-lux italic text-lg text-gold-champagne">{s.subtitle}</p>
                  <p className="mt-5 text-base text-[#A0A0AD] leading-relaxed">{s.desc}</p>
                  <a
                    href={s.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={LINK_TESTIDS[i] || `${id}-section-link`}
                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-gold hover:text-gold-bright transition-colors duration-300 group/link"
                  >
                    {s.linkLabel}
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                    <span aria-hidden="true" className="absolute inset-0 md:pointer-events-none" />
                  </a>
                </div>
                <span aria-hidden="true" className="absolute bottom-0 left-0 h-[2px] w-0 bg-gold group-hover:w-full transition-[width] duration-700 ease-out" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
